import { MESSAGE } from '@/constants/message.constant';
import { createAppSlice } from '../createAppSlice';
import { AppThunk } from '../store';
import { MessageType } from '@/types/common';
import { Message } from '@/enums/message.enum';
import { transformerObject } from '../transformer';

export type AuthSliceState = {
  loading: boolean;
  profileLoading: boolean;
  isAuthenticated: boolean;
  user?: User;
  message?: MessageType;
};

const initialState: AuthSliceState = {
  loading: false,
  profileLoading: true,
  isAuthenticated: false
};

export const authSlice = createAppSlice({
  name: 'auth',
  initialState,
  reducers: (create) => ({
    loginUser: create.asyncThunk(
      async (credentials: {
        displayName: string;
        userPrincipalName: string;
      }) => {
        const response = await apiFetcher.post('auth/login', credentials);
        localStorage.setItem('access_token', response.data.access_token);
        localStorage.setItem('refresh_token', response.data.refresh_token);

        return response.data;
      },
      {
        pending: (state) => {
          state.loading = true;
          state.message = undefined;
        },
        fulfilled: (state, action) => {
          state.loading = false;
          state.isAuthenticated = true;
          state.user = action.payload;
          state.message = {
            type: Message.SUCCESS,
            message: MESSAGE.LOGIN_SUCCESS
          };
        },
        rejected: (state) => {
          state.loading = false;
          state.isAuthenticated = false;
          state.user = undefined;
          state.message = {
            type: Message.ERROR,
            message: MESSAGE.LOGIN_FIELD
          };
        }
      }
    ),
    logoutUser: create.asyncThunk(
      async () => {
        await apiFetcher.get('auth/logout');
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
      },
      {
        pending: (state) => {
          state.loading = true;
        },
        fulfilled: (state) => {
          state.loading = false;
          state.isAuthenticated = false;
          state.user = undefined;
          state.message = {
            type: Message.SUCCESS,
            message: MESSAGE.LOGOUT_SUCCESS
          };
        },
        rejected: (state) => {
          state.loading = false;
          state.message = {
            type: Message.ERROR,
            message: MESSAGE.LOGOUT_FAILED
          };
        }
      }
    ),
    getProfileUser: create.asyncThunk(
      async () => {
        const response = await apiFetcher.get('user');
        return response.data;
      },
      {
        pending: (state) => {
          state.profileLoading = true;
          state.message = undefined;
        },
        fulfilled: (state, action) => {
          state.profileLoading = false;
          state.isAuthenticated = true;
          state.user = transformerObject(action.payload);
        },
        rejected: (state) => {
          state.profileLoading = false;
          state.isAuthenticated = false;
          state.user = undefined;
        }
      }
    ),
    fetchAzureUserInfo: create.asyncThunk(
      async (token: string) => {
        const response = await apiFetcher.get(
          currentCtx.appEnv.microsoftGraphUrl,
          {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
        );
        return response;
      },
      {
        pending: (state) => {
          state.loading = true;
          state.message = undefined;
        },
        fulfilled: (state, action) => {
          state.loading = false;
          state.user = action.payload;
        },
        rejected: (state) => {
          state.loading = false;
          state.message = {
            type: Message.ERROR,
            message: 'Failed to fetch Azure user info'
          };
        }
      }
    )
  }),

  selectors: {
    watchUser: (auth) => auth.user,
    watchAuthState: (auth) => auth,
    watchAuthMessage: (auth) => auth.message,
    watchAuthLoading: (auth) => auth.loading,
    watchProfileLoading: (auth) => auth.profileLoading,
    watchLoggedIn: (auth) => auth.isAuthenticated
  }
});

export const {
  watchUser,
  watchAuthState,
  watchAuthMessage,
  watchLoggedIn,
  watchAuthLoading,
  watchProfileLoading
} = authSlice.selectors;

export const { loginUser, fetchAzureUserInfo, logoutUser, getProfileUser } =
  authSlice.actions;

export const loginWithAzure =
  (token: string): AppThunk =>
  async (dispatch) => {
    const userInfo = await dispatch(fetchAzureUserInfo(token)).unwrap();
    await dispatch(
      loginUser({
        userPrincipalName: userInfo?.userPrincipalName,
        displayName: userInfo?.displayName
      })
    );
  };
